// AuthModal — premium auth experience for Triptic
// Two flows: Sign In (email + password) | Join (role-pick → name/email/password)
// Google OAuth available on both. Pre-OAuth role checks here, post-OAuth in AuthCallback

import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { supabase } from '../../lib/supabase'
import { useWindowWidth } from '../../hooks/useWindowWidth'
import { UserAuth } from '../../context/AuthContext'
import { CALENDLY_URL } from '../../constants'

interface AuthModalProps {
  isOpen: boolean
  onClose: () => void
  defaultMode?: 'signin' | 'signup'
  defaultRole?: 'student' | 'counselor'
}

type Screen = 'role' | 'main' | 'sent' | 'switch'
type Mode = 'signin' | 'signup'
type Role = 'student' | 'counselor'

// Smooth easing curve used everywhere
const EASE = [0.22, 1, 0.36, 1] as const

// Sign-up routes to a booked call instead of creating an account inline.
// Sign-IN (email/password + Google) is untouched.

const fieldLabelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 11,
  fontWeight: 600,
  color: 'rgba(255,255,255,0.5)',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  marginBottom: 6,
}

const fieldInputStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px 14px',
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.10)',
  borderRadius: 10,
  fontSize: 14,
  color: '#fff',
  fontFamily: 'inherit',
  outline: 'none',
  transition: 'border-color 150ms, background 150ms',
  boxSizing: 'border-box',
}

function focusField(e: React.FocusEvent<HTMLInputElement>) {
  e.currentTarget.style.borderColor = 'rgba(108,82,232,0.5)'
  e.currentTarget.style.background = 'rgba(255,255,255,0.06)'
}

function blurField(e: React.FocusEvent<HTMLInputElement>) {
  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.10)'
  e.currentTarget.style.background = 'rgba(255,255,255,0.04)'
}

function AuthModal({ isOpen, onClose, defaultMode, defaultRole }: AuthModalProps) {
  // Sign-in always skips the role picker. Sign-up still chooses a role first,
  // unless the caller already picked one.
  const initialScreen = (): Screen =>
    (defaultMode === 'signin' || defaultRole) ? 'main' : 'role'

  const windowWidth = useWindowWidth()
  const isMobile = windowWidth < 768
  const navigate = useNavigate()
  const { signIn, signUp } = UserAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [loading, setLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [errorAction, setErrorAction] = useState<{ label: string; onClick: () => void } | null>(null)
  const [mode, setMode] = useState<Mode>(defaultMode ?? 'signup')
  const [screen, setScreen] = useState<Screen>(initialScreen)
  const [role, setRole] = useState<Role | null>(defaultRole ?? null)

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setEmail('')
      setPassword('')
      setConfirmPassword('')
      setFirstName('')
      setLastName('')
      setError(null)
      setErrorAction(null)
      setLoading(false)
      setGoogleLoading(false)
      setMode(defaultMode ?? 'signup')
      setScreen(initialScreen())
      setRole(defaultRole ?? null)

      // Surface any error stored by AuthCallback after OAuth redirect
      try {
        const authError = sessionStorage.getItem('auth_error')
        if (authError) {
          setError(authError)
          setErrorAction({
            label: 'OK',
            onClick: () => {
              setError(null)
              setErrorAction(null)
            }
          })
          sessionStorage.removeItem('auth_error')
        }
      } catch { /* ignore */ }
    }
  }, [isOpen, defaultMode, defaultRole])

  // Reset googleLoading when user returns from external OAuth redirect
  // (browser back button, closing Google tab, etc.)
  useEffect(() => {
    if (!isOpen) return

    const resetLoading = () => {
      setGoogleLoading(false)
      setLoading(false)
    }

    window.addEventListener('pageshow', resetLoading)
    const onVisibility = () => {
      if (document.visibilityState === 'visible') resetLoading()
    }
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      window.removeEventListener('pageshow', resetLoading)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [isOpen])

  if (!isOpen) return null

  // ── Helpers ────────────────────────────────────────────────────────

  function clearError() {
    setError(null)
    setErrorAction(null)
  }

  function showError(message: string, action?: { label: string; onClick: () => void }) {
    setError(message)
    setErrorAction(action ?? null)
  }

  // Sign-in never needs a role first.
  function switchToSignIn() {
    clearError()
    setMode('signin')
    setScreen('main')
  }

  function switchToJoin() {
    clearError()
    setMode('signup')
    setScreen(role ? 'main' : 'role')
  }

  // Sign-up path: open the relevant Calendly in a new tab and close the modal.
  // Creates no account and touches no auth state — sign-IN is unaffected.
  function openCall(url: string) {
    clearError()
    window.open(url, '_blank', 'noopener,noreferrer')
    onClose()
  }

  // Check if email exists in students or counselors table
  // Returns: 'student' | 'counselor' | null
  async function checkEmailRole(checkEmail: string): Promise<Role | null> {
    try {
      const { data, error } = await supabase.rpc('check_email_role', {
        p_email: checkEmail.toLowerCase().trim(),
      })

      if (error) {
        // RPC not available or errored — fail open, let post-OAuth checks handle it
        return null
      }

      if (data === 'student' || data === 'counselor') {
        return data as Role
      }
      return null
    } catch {
      return null
    }
  }

  // ── Handlers ───────────────────────────────────────────────────────

  async function handleGoogleSignIn() {
    clearError()
    setGoogleLoading(true)
    try {
      // Persist intent for post-OAuth check in AuthCallback
      // Use BOTH localStorage (survives Google redirect) AND sessionStorage (this tab)
      if (mode === 'signup' && role) {
        try {
          localStorage.setItem('pending_role', role)
          localStorage.setItem('pending_intent', 'signup')
          sessionStorage.setItem('pending_role', role)
          sessionStorage.setItem('pending_intent', 'signup')
        } catch {
          // Safari private mode — ignore
        }
      } else if (mode === 'signin') {
        try {
          localStorage.setItem('pending_intent', 'signin')
          sessionStorage.setItem('pending_intent', 'signin')
          // Clear any stale pending_role from previous attempts
          localStorage.removeItem('pending_role')
          sessionStorage.removeItem('pending_role')
        } catch { /* ignore */ }
      }

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: window.location.origin + '/auth/callback' },
      })
      if (error) {
        showError(error.message)
        setGoogleLoading(false)
      }
      // On success, browser redirects to Google — no further code runs here
    } catch {
      showError('Something went wrong. Please try again.')
      setGoogleLoading(false)
    }
  }

  // Route via "/" so LandingRedirect picks the portal from the profile row that
  // actually exists. Trusting the chosen role here would drop a user into a
  // portal with no matching row, where every query fails RLS.
  function goToPortal(_forRole: Role | null) {
    onClose()
    navigate('/')
  }

  async function handlePasswordSubmit(e: React.FormEvent) {
    e.preventDefault()
    clearError()
    setLoading(true)

    const trimmedEmail = email.toLowerCase().trim()

    try {
      // One email maps to exactly one role, so check before touching auth.
      const existingRole = await checkEmailRole(trimmedEmail)

      if (mode === 'signin') {
        if (existingRole && role && existingRole !== role) {
          showError(
            `This email is registered as a ${existingRole}, not a ${role}.`,
            {
              label: `Continue as ${existingRole}`,
              onClick: () => { clearError(); setRole(existingRole) },
            }
          )
          setLoading(false)
          return
        }

        const result = await signIn(trimmedEmail, password)
        if (!result?.success) {
          showError(
            result?.error?.message ?? 'The email or password you entered is incorrect.'
          )
          setLoading(false)
          return
        }

        const signedInRole =
          result.data?.user?.user_metadata?.role ?? existingRole ?? role
        goToPortal(signedInRole)
        return
      }

      // ── signup ──
      if (!role) {
        showError('Please select a role first.')
        setLoading(false)
        return
      }

      if (existingRole === role) {
        showError(
          `You already have a ${role} account with this email.`,
          { label: 'Sign in instead', onClick: switchToSignIn }
        )
        setLoading(false)
        return
      }

      if (existingRole && existingRole !== role) {
        showError(
          `This email is already registered as a ${existingRole}. ` +
          `Each email can only have one role.`,
          { label: `Sign in as ${existingRole}`, onClick: switchToSignIn }
        )
        setLoading(false)
        return
      }

      if (password.length < 6) {
        showError('Password must be at least 6 characters.')
        setLoading(false)
        return
      }

      if (password !== confirmPassword) {
        showError('Passwords do not match.')
        setLoading(false)
        return
      }

      const result = await signUp({
        first: firstName.trim(),
        last: lastName.trim(),
        email: trimmedEmail,
        password1: password,
        password2: confirmPassword,
        role,
        acceptedTerms: true,
        acceptedPrivacy: true,
      })

      if (!result?.success) {
        showError(result?.error?.message ?? 'Could not create your account.')
        setLoading(false)
        return
      }

      // With email confirmation enabled Supabase returns no session yet.
      if (!result.data?.session) {
        setScreen('sent')
        setLoading(false)
        return
      }

      goToPortal(role)
    } catch {
      showError('Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  // ── UI ─────────────────────────────────────────────────────────────

  const canSubmit =
    mode === 'signin'
      ? Boolean(email && password)
      : Boolean(firstName && lastName && email && password && confirmPassword)

  const heading =
    screen === 'role' ? (mode === 'signin' ? 'Sign in to Triptic' : 'Join Triptic') :
    screen === 'sent' ? 'Check your inbox' :
    mode === 'signin' ? 'Welcome back' :
    role === 'counselor' ? 'Join as Counselor' :
    role === 'student' ? 'Join as Student' :
    'Create your account'

  const subtext =
    screen === 'role' ? 'Tell us who you are — we\'ll set you up on a quick call' :
    // Kept from main: the 'sent' screen belongs to the email signup path, which
    // is now unreachable but retained.
    screen === 'sent' ? `Confirm your email to finish creating your account` :
    mode === 'signin' ? 'Sign in to continue' :
    role === 'counselor' ? 'Showcase your outcomes and grow your practice' :
    role === 'student' ? 'Find verified counselors for your dream schools' :
    ''

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={e => { if (e.target === e.currentTarget) onClose() }}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(3,1,10,0.78)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          padding: 16,
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.96 }}
          transition={{ duration: 0.35, ease: EASE }}
          style={{
            position: 'relative',
            width: '100%',
            maxWidth: 440,
            background: 'linear-gradient(155deg, rgba(20,14,35,0.96), rgba(10,5,18,0.98))',
            border: '1px solid rgba(108,82,232,0.28)',
            borderRadius: 20,
            padding: isMobile ? '32px 24px 28px' : '40px 36px 32px',
            boxShadow:
              '0 32px 80px rgba(0,0,0,0.7), 0 0 60px rgba(108,82,232,0.08), inset 0 1px 0 rgba(255,255,255,0.05)',
            fontFamily: "'Inter', system-ui, sans-serif",
          }}
        >
          {/* Close button */}
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              position: 'absolute',
              top: 16,
              right: 16,
              width: 32,
              height: 32,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'transparent',
              border: 'none',
              borderRadius: 8,
              color: 'rgba(255,255,255,0.4)',
              fontSize: 20,
              cursor: 'pointer',
              transition: 'all 150ms ease',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.06)'
              e.currentTarget.style.color = 'rgba(255,255,255,0.9)'
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = 'transparent'
              e.currentTarget.style.color = 'rgba(255,255,255,0.4)'
            }}
          >
            ×
          </button>

          {/* Header */}
          <div style={{ marginBottom: 28 }}>
            <motion.h2
              key={`heading-${screen}-${mode}-${role}`}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, ease: EASE }}
              style={{
                margin: 0,
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                fontSize: isMobile ? 26 : 30,
                fontWeight: 600,
                color: '#F8F7FF',
                letterSpacing: '-0.01em',
                lineHeight: 1.1,
              }}
            >
              {heading}
            </motion.h2>
            <motion.p
              key={`subtext-${screen}-${mode}-${role}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.05 }}
              style={{
                margin: '6px 0 0',
                fontSize: 13,
                color: 'rgba(255,255,255,0.5)',
                fontWeight: 400,
              }}
            >
              {subtext}
            </motion.p>
          </div>

          {/* Inline Error */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, height: 0, marginBottom: 0 }}
                animate={{ opacity: 1, height: 'auto', marginBottom: 18 }}
                exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                transition={{ duration: 0.25, ease: EASE }}
                style={{ overflow: 'hidden' }}
              >
                <div
                  style={{
                    padding: '12px 14px',
                    background: 'rgba(220,38,38,0.08)',
                    border: '1px solid rgba(220,38,38,0.25)',
                    borderRadius: 10,
                    fontSize: 13,
                    color: '#fca5a5',
                    lineHeight: 1.5,
                  }}
                >
                  <div style={{ marginBottom: errorAction ? 8 : 0 }}>{error}</div>
                  {errorAction && (
                    <button
                      onClick={errorAction.onClick}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#fca5a5',
                        textDecoration: 'underline',
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: 'pointer',
                        padding: 0,
                        fontFamily: 'inherit',
                      }}
                    >
                      {errorAction.label} →
                    </button>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Screen content with smooth transitions */}
          <AnimatePresence mode="wait">
            {/* ── Role Picker ── */}
            {screen === 'role' && (
              <motion.div
                key="role"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                transition={{ duration: 0.25, ease: EASE }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <RoleCard
                    icon={<StudentIcon />}
                    label="I'm a Student"
                    description="Book a free 15-min call to get set up."
                    accent="168,85,247"
                    accentText="#c084fc"
                    onClick={() => openCall(CALENDLY_URL)}
                  />
                  <RoleCard
                    icon={<CounselorIcon />}
                    label="I'm a Counselor"
                    description="Book a demo to bring your students onto Triptic."
                    accent="236,72,153"
                    accentText="#f472b6"
                    onClick={() => openCall(CALENDLY_URL)}
                  />
                  <RoleCard
                    icon={<InstitutionIcon />}
                    label="I'm an Institution / School"
                    description="Book a demo to see the whole-school dashboard."
                    accent="230,168,23"
                    accentText="#f0c45a"
                    onClick={() => openCall(CALENDLY_URL)}
                  />
                </div>

                <div
                  style={{
                    marginTop: 22,
                    paddingTop: 18,
                    borderTop: '1px solid rgba(255,255,255,0.06)',
                    textAlign: 'center',
                  }}
                >
                  <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>
                    {mode === 'signin' ? 'New to Triptic? ' : 'Already have an account? '}
                  </span>
                  <button
                    onClick={mode === 'signin' ? switchToJoin : switchToSignIn}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: '#a78bfa',
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                      padding: 0,
                      fontFamily: 'inherit',
                    }}
                  >
                    {mode === 'signin' ? 'Create account →' : 'Sign in →'}
                  </button>
                </div>
              </motion.div>
            )}

            {/* ── Main (email + google) ── */}
            {screen === 'main' && (
              <motion.div
                key="main"
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                transition={{ duration: 0.25, ease: EASE }}
              >
                {/* Back to role picker — sign-up only; sign-in has no role step */}
                {mode === 'signup' && !defaultRole && (
                  <button
                    onClick={() => {
                      clearError()
                      setScreen('role')
                      setRole(null)
                    }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: 'rgba(255,255,255,0.5)',
                      fontSize: 13,
                      cursor: 'pointer',
                      padding: '0 0 16px 0',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      fontFamily: 'inherit',
                      transition: 'color 150ms',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.color = '#fff')}
                    onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.5)')}
                  >
                    ← Back
                  </button>
                )}

                {/* Google OAuth button */}
                <button
                  onClick={handleGoogleSignIn}
                  disabled={googleLoading || loading}
                  style={{
                    width: '100%',
                    padding: '13px 16px',
                    background: googleLoading ? '#e8e8e8' : '#ffffff',
                    color: '#1a1a1a',
                    border: 'none',
                    borderRadius: 10,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: googleLoading ? 'not-allowed' : 'pointer',
                    fontFamily: 'inherit',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 10,
                    transition: 'all 150ms ease',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
                  }}
                  onMouseEnter={e => {
                    if (!googleLoading) {
                      e.currentTarget.style.background = '#f3f4f6'
                      e.currentTarget.style.transform = 'translateY(-1px)'
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.4)'
                    }
                  }}
                  onMouseLeave={e => {
                    if (!googleLoading) {
                      e.currentTarget.style.background = '#ffffff'
                      e.currentTarget.style.transform = 'translateY(0)'
                      e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.3)'
                    }
                  }}
                >
                  <GoogleIcon />
                  {googleLoading ? 'Redirecting…' : 'Continue with Google'}
                </button>

                {/* Divider */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0' }}>
                  <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.08)' }} />
                  <span
                    style={{
                      fontSize: 10,
                      color: 'rgba(255,255,255,0.35)',
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      fontWeight: 500,
                    }}
                  >
                    or with email
                  </span>
                  <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.08)' }} />
                </div>

                {/* Email + password form */}
                <form onSubmit={handlePasswordSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {mode === 'signup' && (
                    <div style={{ display: 'flex', gap: 10 }}>
                      <div style={{ flex: 1 }}>
                        <label style={fieldLabelStyle}>First name</label>
                        <input
                          type="text"
                          value={firstName}
                          onChange={e => { clearError(); setFirstName(e.target.value) }}
                          placeholder="Jane"
                          required
                          autoFocus
                          style={fieldInputStyle}
                          onFocus={focusField}
                          onBlur={blurField}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={fieldLabelStyle}>Last name</label>
                        <input
                          type="text"
                          value={lastName}
                          onChange={e => { clearError(); setLastName(e.target.value) }}
                          placeholder="Doe"
                          required
                          style={fieldInputStyle}
                          onFocus={focusField}
                          onBlur={blurField}
                        />
                      </div>
                    </div>
                  )}

                  <div>
                    <label style={fieldLabelStyle}>Email address</label>
                    <input
                      type="email"
                      value={email}
                      onChange={e => { clearError(); setEmail(e.target.value) }}
                      placeholder="you@example.com"
                      required
                      autoFocus={mode === 'signin'}
                      style={fieldInputStyle}
                      onFocus={focusField}
                      onBlur={blurField}
                    />
                  </div>

                  <div>
                    <label style={fieldLabelStyle}>Password</label>
                    <input
                      type="password"
                      value={password}
                      onChange={e => { clearError(); setPassword(e.target.value) }}
                      placeholder={mode === 'signup' ? 'At least 6 characters' : '••••••••'}
                      required
                      autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                      style={fieldInputStyle}
                      onFocus={focusField}
                      onBlur={blurField}
                    />
                  </div>

                  {mode === 'signup' && (
                    <div>
                      <label style={fieldLabelStyle}>Confirm password</label>
                      <input
                        type="password"
                        value={confirmPassword}
                        onChange={e => { clearError(); setConfirmPassword(e.target.value) }}
                        placeholder="Re-enter your password"
                        required
                        autoComplete="new-password"
                        style={fieldInputStyle}
                        onFocus={focusField}
                        onBlur={blurField}
                      />
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading || googleLoading || !canSubmit}
                    style={{
                      width: '100%',
                      padding: '13px',
                      marginTop: 4,
                      background:
                        loading || !canSubmit
                          ? 'rgba(108,82,232,0.25)'
                          : 'linear-gradient(135deg, #6c52e8, #8b5cf6)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 10,
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: loading || !canSubmit ? 'not-allowed' : 'pointer',
                      fontFamily: 'inherit',
                      transition: 'all 150ms ease',
                      boxShadow: !canSubmit ? 'none' : '0 4px 12px rgba(108,82,232,0.25)',
                    }}
                    onMouseEnter={e => {
                      if (!loading && canSubmit) {
                        e.currentTarget.style.transform = 'translateY(-1px)'
                        e.currentTarget.style.boxShadow = '0 8px 20px rgba(108,82,232,0.4)'
                      }
                    }}
                    onMouseLeave={e => {
                      if (!loading && canSubmit) {
                        e.currentTarget.style.transform = 'translateY(0)'
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(108,82,232,0.25)'
                      }
                    }}
                  >
                    {loading
                      ? (mode === 'signin' ? 'Signing in…' : 'Creating account…')
                      : (mode === 'signin' ? 'Sign in →' : 'Create account →')}
                  </button>
                </form>

                {/* Switch flow link */}
                <div
                  style={{
                    marginTop: 20,
                    paddingTop: 16,
                    borderTop: '1px solid rgba(255,255,255,0.06)',
                    textAlign: 'center',
                  }}
                >
                  {mode === 'signin' ? (
                    <>
                      <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>
                        New to Triptic?{' '}
                      </span>
                      <button
                        onClick={switchToJoin}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: '#a78bfa',
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: 'pointer',
                          padding: 0,
                          fontFamily: 'inherit',
                        }}
                      >
                        Create account →
                      </button>
                    </>
                  ) : (
                    <>
                      <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>
                        Already have an account?{' '}
                      </span>
                      <button
                        onClick={switchToSignIn}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: '#a78bfa',
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: 'pointer',
                          padding: 0,
                          fontFamily: 'inherit',
                        }}
                      >
                        Sign in →
                      </button>
                    </>
                  )}
                </div>
              </motion.div>
            )}

            {/* ── Sent confirmation ── */}
            {screen === 'sent' && (
              <motion.div
                key="sent"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.3, ease: EASE }}
                style={{ textAlign: 'center', padding: '8px 0 0' }}
              >
                <div
                  style={{
                    width: 60,
                    height: 60,
                    margin: '0 auto 20px',
                    borderRadius: '50%',
                    background:
                      'linear-gradient(135deg, rgba(108,82,232,0.2), rgba(139,92,246,0.15))',
                    border: '1px solid rgba(108,82,232,0.4)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <MailIcon />
                </div>

                <p
                  style={{
                    margin: '0 0 8px',
                    fontSize: 15,
                    color: '#fff',
                    fontWeight: 500,
                  }}
                >
                  Confirm your email
                </p>
                <p
                  style={{
                    margin: '0 0 24px',
                    fontSize: 13,
                    color: 'rgba(255,255,255,0.55)',
                    lineHeight: 1.6,
                  }}
                >
                  We sent a confirmation link to <br />
                  <span style={{ color: '#fff', fontWeight: 500 }}>{email}</span>
                  <br />
                  Click it to activate your account, then sign in.
                </p>

                <button
                  onClick={() => { setScreen('main'); clearError() }}
                  style={{
                    padding: '10px 18px',
                    background: 'transparent',
                    border: '1px solid rgba(255,255,255,0.15)',
                    borderRadius: 8,
                    fontSize: 12,
                    color: 'rgba(255,255,255,0.7)',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    transition: 'all 150ms',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)'
                    e.currentTarget.style.color = '#fff'
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'
                    e.currentTarget.style.color = 'rgba(255,255,255,0.7)'
                  }}
                >
                  Use a different email
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

// ─── Sub-components ─────────────────────────────────────────────────

interface RoleCardProps {
  icon: React.ReactNode
  label: string
  description: string
  onClick: () => void
  /** "r,g,b" accent triple. Defaults to the original violet. */
  accent?: string
  /** Arrow/keyline colour on hover. Defaults to the original violet. */
  accentText?: string
}

function RoleCard({ icon, label, description, onClick, accent = '108,82,232', accentText = '#a78bfa' }: RoleCardProps) {
  const [hovered, setHovered] = useState(false)
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        width: '100%',
        padding: '16px 18px',
        background: hovered
          ? `linear-gradient(135deg, rgba(${accent},0.14), rgba(${accent},0.06))`
          : `rgba(${accent},0.04)`,
        border: `1px solid ${hovered ? `rgba(${accent},0.5)` : `rgba(${accent},0.18)`}`,
        borderRadius: 12,
        cursor: 'pointer',
        textAlign: 'left',
        transition: 'all 200ms ease',
        fontFamily: 'inherit',
        transform: hovered ? 'translateY(-1px)' : 'translateY(0)',
        boxShadow: hovered
          ? `0 8px 24px rgba(${accent},0.18)`
          : '0 1px 3px rgba(0,0,0,0.2)',
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: 10,
          background: hovered ? `rgba(${accent},0.25)` : `rgba(${accent},0.12)`,
          border: `1px solid rgba(${accent},0.3)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          transition: 'all 200ms ease',
        }}
      >
        {icon}
      </div>
      <div style={{ flex: 1 }}>
        <div
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: '#fff',
            marginBottom: 3,
          }}
        >
          {label}
        </div>
        <div
          style={{
            fontSize: 12,
            color: 'rgba(255,255,255,0.5)',
            lineHeight: 1.5,
          }}
        >
          {description}
        </div>
      </div>
      <div
        style={{
          color: hovered ? accentText : 'rgba(255,255,255,0.3)',
          fontSize: 16,
          transition: 'all 200ms ease',
          transform: hovered ? 'translateX(2px)' : 'translateX(0)',
        }}
      >
        →
      </div>
    </button>
  )
}

function StudentIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
      <path d="M6 12v5c3 3 9 3 12 0v-5" />
    </svg>
  )
}

function CounselorIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#f472b6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  )
}

function InstitutionIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#f0c45a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 21h18" />
      <path d="M5 21V9l7-5 7 5v12" />
      <path d="M10 21v-5h4v5" />
    </svg>
  )
}

function MailIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m22 7-10 5L2 7" />
    </svg>
  )
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M17.64 9.2045c0-.6381-.0573-1.2518-.1636-1.8409H9v3.4814h4.8436c-.2086 1.125-.8427 2.0782-1.7959 2.7164v2.2581h2.9087c1.7018-1.5668 2.6836-3.874 2.6836-6.615z" fill="#4285F4" />
      <path d="M9 18c2.43 0 4.4673-.806 5.9564-2.1805l-2.9087-2.2581c-.8064.54-1.8382.859-3.0477.859-2.3441 0-4.3282-1.5832-5.036-3.7104H.9574v2.3318C2.4382 15.9832 5.4818 18 9 18z" fill="#34A853" />
      <path d="M3.964 10.71c-.18-.54-.2818-1.1168-.2818-1.71s.1018-1.17.2818-1.71V4.9582H.9574C.3477 6.1732 0 7.5477 0 9s.3477 2.8268.9574 4.0418L3.964 10.71z" fill="#FBBC05" />
      <path d="M9 3.5795c1.3214 0 2.5077.4541 3.4405 1.346l2.5813-2.5814C13.4627.8918 11.4255 0 9 0 5.4818 0 2.4382 2.0168.9574 4.9582L3.964 7.29C4.6718 5.1627 6.6559 3.5795 9 3.5795z" fill="#EA4335" />
    </svg>
  )
}

export default AuthModal
